import React, { useMemo, useRef, useState } from 'react'
import styles from "../css/Settings.module.css"
import { IoEyeOffOutline } from "react-icons/io5";
import { IoEyeOutline } from "react-icons/io5";
import { AiOutlineArrowLeft, AiOutlineArrowRight, AiOutlineCheck } from "react-icons/ai";
import LoadingButton from '../../Loaders/js/LoadingButton';
import { useDispatch, useSelector } from 'react-redux';
import { AUTH_RESET, AUTH_SUCCESS } from '../../../actions/authTypes';
import { updatePassword } from '../../../actions/authActions';
import { useEffect } from 'react';
import useOverflow from '../../../hooks/useOverflow';
import DemoModeBanner from './DemoModeBanner';

const Password = ({goBack}) => {
    const [data,setData] = useState({current_password:"",new_password:"",confirm_password:""})
    const [showPassword,setShowPassword] = useState(false)
    const [errors,setErrors] = useState({})
    const loading = useSelector((state) => state.authState.loading)
    const success = useSelector((state) => state.authState.authSuccess)
    const user = useSelector((state) => state.authState.user)
    const dispatch = useDispatch()
    const contentRef = useRef(null)
    const hasOverflow = useOverflow(contentRef)

    useEffect(()=>{
        return () => {
            dispatch({type: AUTH_RESET})
        }
    },[dispatch])

    const resetErrors = (name, value) => {
        if (
            errors.confirm_password &&
            ((name === "new_password" && value === data.confirm_password) ||
            (name === "confirm_password" && value === data.new_password))
        ){
            setErrors((prevErrors)=>{
                return {...prevErrors,confirm_password: null}
            })
        }
        if (name === "new_password" && errors.new_password && value.length >= 8){
            setErrors((prevErrors)=>{
                return {...prevErrors,new_password: null}
            })
        }
        if (name === "current_password" && errors.current_password) {
            setErrors((prevErrors)=>{
                return {...prevErrors,current_password: null}
            })
        }
    }

    const handleChange = (e) => {
        let name = e.target.name
        let value = e.target.value
        setData({...data,[name]:value})
        resetErrors(name,value)
        if (success){
            dispatch({type: AUTH_SUCCESS,payload:false})
        }
    }

    const handleSubmit = async(e) => {
        e.preventDefault()
        document.activeElement.blur()
        let valid = true
        if (data.new_password.length < 8){
          valid = false
          setErrors((prevErrors)=> {return {...prevErrors,new_password:"At least 8 characters"}})
        }
        if (data.new_password !== data.confirm_password){
          valid = false
          setErrors((prevErrors)=> {return {...prevErrors,confirm_password:"Passwords do not match"}})
        }
        if (valid){
            try{
                await dispatch(updatePassword({current_password:data.current_password, new_password: data.new_password}))
                setData({current_password:"",new_password:"",confirm_password:""})
            }
            catch(err){
                setErrors({...errors,...err.response.data})
            }
        }
    }

    const isDisabled = useMemo(()=>{
        if (!data.confirm_password || !data.new_password || !data.current_password){
            return true
        }
        else if (Object.values(errors).some(err => err !== null)){
            return true
        }
        else{
            return user?.isDemoUser
        }
    },[data,errors,user?.isDemoUser])

    return (
        <form className = {`${styles.form} ${hasOverflow ? styles.overflowed : ""} nested-container`} onSubmit = {handleSubmit}>
            <div className = {styles.subpageHeader}>
                <button type = "button" className = {styles.back} onClick = {()=>goBack()}>
                    <AiOutlineArrowLeft/>
                </button>
                <div>
                    <div className = {styles.subpageTitle}>Change Password</div>
                    <div className = {styles.subpageDesc}>
                        Your password must be at least 8 characters long.
                    </div>
                </div>
            </div>
            {user.isDemoUser && <DemoModeBanner tab = "password"/>}
            <div className = {`${styles.body} nested-container`} ref = {contentRef}>
                {success && <div data-testid = "success-message" className = {styles.successBanner}>
                    <div className = {styles.checkIcon}><AiOutlineCheck/></div>
                    <div className = {styles.successText}>Password changed successfully</div>
                </div>}
                <div className = {`${styles.field} ${errors.current_password ? styles.errorBox : ""}`}>
                    <label htmlFor = "current_password" className = {styles.label}>Current Password</label>
                    <div className = {styles.inputWrap}>
                        <div className = {styles.inputContainer}>
                            <input
                                className = {styles.input}
                                type = {showPassword ? "text" : "password"}
                                name = "current_password" 
                                value = {data.current_password}
                                onChange = {handleChange}
                                placeholder="Enter your current password"
                                disabled = {user.isDemoUser}
                            />
                        </div>
                        {!user.isDemoUser && <button
                            type="button"
                            className = {styles.inputIcon}
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            {showPassword ? <IoEyeOffOutline/> : <IoEyeOutline/>}
                        </button>}
                    </div>
                    {errors.current_password && <div data-testid = "current-password-error" className = {styles.error}>{errors.current_password}</div>}
                </div>
                <div className = {`${styles.field} ${errors.new_password ? styles.errorBox : ""}`}>
                    <label htmlFor = "new_password" className = {styles.label}>New Password</label>
                    <div className = {styles.inputContainer}>
                        <input
                            className = {styles.input}
                            type = "password" 
                            name = "new_password" 
                            value = {data.new_password} 
                            onChange = {handleChange}
                            placeholder = "Enter new password"
                            disabled = {user.isDemoUser}
                        />
                    </div>
                    {errors.new_password && <div data-testid = "new-password-error" className = {styles.error}>{errors.new_password}</div>}
                </div>
                <div className = {`${styles.field} ${errors.confirm_password ? styles.errorBox : ""}`}>
                    <label htmlFor = "confirm_password" className = {styles.label}>Confirm New Password</label>
                    <div className = {styles.inputContainer}>
                        <input
                            className = {styles.input}
                            type = "password" 
                            name = "confirm_password" 
                            value = {data.confirm_password} 
                            onChange = {handleChange}
                            placeholder = "Re-enter new password"
                            disabled = {user.isDemoUser}
                        />
                    </div>
                    {errors.confirm_password && <div data-testid = "confirm-password-error" className = {styles.error}>{errors.confirm_password}</div>}
                    <div className = {styles.hint}>
                        After changing, you'll be signed out of all sessions.
                    </div>
                </div>
            </div>
            <div className = {styles.footer}>
                <button type = "button" className = {styles.cancelBtn} onClick = {()=>goBack()}>Cancel</button>
                <button type = "submit" className = {`${styles.submitBtn} ${loading ? styles.loading : ""}`} disabled = {isDisabled} >
                    Change Password
                    <div className = {styles.btnIcon}>
                        <AiOutlineArrowRight/>
                    </div>
                    {loading && <LoadingButton/>}
                </button>
            </div>
        </form>
  )
}

export default Password